import CapacityFilter from './CapacityFilter';
import FeatureFilter from './FeatureFilter';
import { FeatureFilterItemType } from './FeatureFilterItemType';
import FloorFilter from './FloorFilter';
import getStore from '../../store/store';
import { ITheme } from '@uifabric/styling';
import { KeyCodes } from '@uifabric/utilities';
import { observer } from 'mobx-react-lite';
import { ActionButton, DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { Icon } from '@fluentui/react/lib/Icon';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { ControlIcons } from 'owa-control-icons';
import loc, { format } from 'owa-localize';
import { clearButtonText } from 'owa-locstrings/lib/strings/clearbuttontext.locstring.json';
import {
    featureFilterPlaceHolder,
    capabilityFilterLabel,
    capacityFilterHeader,
    floorFilterHeader,
    applyButtonText,
    capacityFilterLabel,
    featureFilterLabel,
    floorFilterLabel,
    featureFilterAudioCheckboxLabel,
    featureFilterDisplayCheckboxLabel,
    featureFilterVideoCheckboxLabel,
    featureFilterAccessibleLabel,
} from './CapabilityFilters.locstring.json';
import getRoomlistCapabilities from '../../selectors/getRoomlistCapabilities';
import * as React from 'react';
import { Dropdown, IDropdownOption, DropdownMenuItemType } from '@fluentui/react/lib/Dropdown';
import {
    updateCapacityExpandation,
    updateFloorExpandation,
    updateFeatureExpandation,
    clearFilters,
    updateSelectedFilters,
} from '../../mutators/capabilityFilterMutators';
import CapabilityFilter, { CapabilityFilterType } from './CapabilityFilterType';
import {
    isFilterOpened,
    isFilterApplied,
    updateCapabilitiesAvailability,
} from '../../mutators/roomPanelMetricsMutators';

const styles = require('./CapabilityFilters.scss');
const classNames = require('classnames/bind').bind(styles);
const COMMA = ', ';

const ROOMCAPABILITYLABELS = {
    [CapabilityFilterType.Capacity]: loc(capacityFilterLabel),
    [CapabilityFilterType.Feature]: loc(featureFilterLabel),
    [CapabilityFilterType.Floor]: loc(floorFilterLabel),
};

const FEATUREFILTERLABELS = {
    [FeatureFilterItemType.Audio]: loc(featureFilterAudioCheckboxLabel),
    [FeatureFilterItemType.Display]: loc(featureFilterDisplayCheckboxLabel),
    [FeatureFilterItemType.Video]: loc(featureFilterVideoCheckboxLabel),
    [FeatureFilterItemType.Accessible]: loc(featureFilterAccessibleLabel),
};

export interface CapabilityFiltersProps {
    className: string;
    theme: ITheme;
}

export const CapabilityFilters = observer(function CapabilityFilters(
    props: CapabilityFiltersProps
) {
    const roomListCapabilities = getRoomlistCapabilities();
    const onRenderPlaceholder = (): JSX.Element => {
        return <span>{getFilterSummaryPlaceholder()}</span>;
    };
    // generate the summarized placeholder string of the filter and tooltip,
    // if there's no filter value selected, default placeholder should be "Filters";
    // otherwise it's generated based on selected filter values, ex: "Capacity: 8+, Audio, Floor: 3"
    const getFilterSummaryPlaceholder = (): string => {
        const { selectedFilters } = getStore().roomFilterState;
        if (!selectedFilters || selectedFilters.length === 0) {
            return loc(capabilityFilterLabel);
        }
        let placeholders: string[] = [];
        selectedFilters.forEach(filter => getCapabilityPlaceholder(placeholders, filter));
        return placeholders.length > 0 ? placeholders.join(COMMA) : loc(capabilityFilterLabel);
    };
    // generate the placeholder value for each selected capability
    const getCapabilityPlaceholder = (placeholders: string[], filter: CapabilityFilter) => {
        switch (filter.filterName) {
            case CapabilityFilterType.Capacity:
                // only use selected capacity when the capacity filter is enabled
                if (roomListCapabilities.maxCapacity) {
                    placeholders.push(format(loc(capacityFilterHeader), filter.filterValue));
                }
                break;
            case CapabilityFilterType.Feature:
                (filter.filterValue as FeatureFilterItemType[]).forEach(feature =>
                    placeholders.push(FEATUREFILTERLABELS[feature])
                );
                break;
            case CapabilityFilterType.Floor:
                // only use selected floor when the floor filter is enabled
                if (isFinite(roomListCapabilities.minFloor)) {
                    placeholders.push(format(loc(floorFilterHeader), filter.filterValue));
                }
                break;
            case CapabilityFilterType.Tag:
                (filter.filterValue as string[]).forEach(tag => placeholders.push(tag));
                break;
            default:
                break;
        }
    };
    // generate filter options for existing capability filter types
    const getOptions = (): IDropdownOption[] => {
        const options: IDropdownOption[] = [];
        const filters = Object.keys(CapabilityFilterType).filter(
            k => typeof CapabilityFilterType[k as any] === 'number'
        );
        filters.forEach(filter => {
            options.push(generateFilterOption(CapabilityFilterType[filter]));
        });
        const divider: IDropdownOption = {
            key: 'divider',
            text: '-',
            itemType: DropdownMenuItemType.Divider,
        };
        options.push(divider);
        const applyButton: IDropdownOption = {
            key: loc(applyButtonText),
            text: getFilterSummaryPlaceholder(),
            itemType: DropdownMenuItemType.Normal,
        };
        options.push(applyButton);
        return options;
    };
    const generateFilterOption = (filter: CapabilityFilterType): IDropdownOption => {
        return {
            key: filter,
            text: ROOMCAPABILITYLABELS[filter],
            data: getFilterOptionData(filter),
            // the filters are rendered as header type since they're not the selection options
            itemType: DropdownMenuItemType.Header,
        } as IDropdownOption;
    };
    const getFilterOptionData = (filter: CapabilityFilterType) => {
        const roomFilterState = getStore().roomFilterState;
        switch (filter) {
            case CapabilityFilterType.Capacity:
                return {
                    expanded: roomFilterState.expandCapacity,
                    onClick: () => {
                        updateCapacityExpandation();
                    },
                    renderFilter: renderCapacityFilter(),
                };
            case CapabilityFilterType.Feature:
                return {
                    expanded: roomFilterState.expandFeature,
                    onClick: () => {
                        updateFeatureExpandation();
                    },
                    renderFilter: renderFeatureFilter(),
                };
            case CapabilityFilterType.Floor:
                return {
                    expanded: roomFilterState.expandFloor,
                    onClick: () => {
                        updateFloorExpandation();
                    },
                    renderFilter: renderFloorFilter(),
                };
            default:
                return null;
        }
    };
    const renderCapacityFilter = (): JSX.Element => {
        return getStore().roomFilterState.expandCapacity ? (
            <div className={styles.detailFilterContainer}>
                <CapacityFilter maxCapacity={roomListCapabilities.maxCapacity} />
            </div>
        ) : null;
    };
    const renderFeatureFilter = (): JSX.Element => {
        // set features selection from store value
        const { expandFeature } = getStore().roomFilterState;
        return expandFeature ? (
            <div className={styles.detailFilterContainer}>
                <FeatureFilter
                    theme={props.theme}
                    hasFeatures={roomListCapabilities.hasFeatures}
                    placeholder={loc(featureFilterPlaceHolder)}
                    disabled={false}
                />
            </div>
        ) : null;
    };
    const renderFloorFilter = (): JSX.Element => {
        return getStore().roomFilterState.expandFloor ? (
            <div className={styles.detailFilterContainer}>
                <FloorFilter
                    minFloor={roomListCapabilities.minFloor}
                    maxFloor={roomListCapabilities.maxFloor}
                />
            </div>
        ) : null;
    };
    const roomFilterState = getStore().roomFilterState;
    // display clear button if there's filter selected and applied
    const hasFiltersSelected =
        (roomFilterState.selectedCapacity ||
            (roomFilterState.selectedFeatures && roomFilterState.selectedFeatures.length > 0) ||
            roomFilterState.selectedFloor) &&
        roomFilterState.selectedFilters &&
        roomFilterState.selectedFilters.length > 0;
    const placeholder = getFilterSummaryPlaceholder();
    updateCapabilitiesAvailability();
    return (
        <div className={classNames(props.className)} onKeyDownCapture={onKeyDown}>
            <TooltipHost
                // only display the tooltip when there're filters selected and applied
                content={hasFiltersSelected ? undefined : placeholder}
                calloutProps={{ gapSpace: 0 }}>
                <Dropdown
                    onRenderPlaceholder={onRenderPlaceholder}
                    onRenderOption={onRenderOption}
                    options={getOptions()}
                    styles={{
                        dropdown: styles.capabilityDropdown,
                        title: styles.capabilityTitle,
                        dropdownItemHeader: styles.capabilityDropdownItemHeader,
                        dropdownItemDisabled: styles.capabilityDropdownItemDisabled,
                        dropdownItemSelected: styles.capabilityDropdownItemSelected,
                    }}
                    className={styles.filterDropdown}
                    calloutProps={{
                        className: styles.filterCallout,
                    }}
                />
            </TooltipHost>
            {hasFiltersSelected && (
                <ActionButton
                    className={styles.clearButton}
                    data-is-focusable={true}
                    ariaLabel={loc(clearButtonText)}
                    role="button"
                    onClick={clearFilters}>
                    {loc(clearButtonText)}
                </ActionButton>
            )}
        </div>
    );
});

function onApplyClick() {
    updateSelectedFilters();
    isFilterApplied();
}

function onRenderOption(option: IDropdownOption): JSX.Element {
    let optionToRender = null;
    // if the item to be rendered is a filter
    if (option.data && option.itemType === DropdownMenuItemType.Header) {
        optionToRender = (
            <div className={styles.optionItemContainer}>
                <DefaultButton
                    key={option.key}
                    title={option.text}
                    aria-label={option.text}
                    onClick={option.itemType === DropdownMenuItemType.Header && option.data.onClick}
                    className={styles.filterHeader}>
                    {
                        <Icon
                            className={styles.icon}
                            iconName={
                                option.itemType === DropdownMenuItemType.Header &&
                                option.data.expanded
                                    ? ControlIcons.ChevronDown
                                    : ControlIcons.ChevronRight
                            }
                            aria-hidden={true}
                            title={option.text}
                        />
                    }
                    <span>{option.text}</span>
                </DefaultButton>
                <div>
                    {option.itemType === DropdownMenuItemType.Header && option.data.renderFilter}
                </div>
            </div>
        );
    } else if (option.itemType === DropdownMenuItemType.Normal) {
        // if the item to be rendered is the apply button
        optionToRender = (
            <PrimaryButton
                className={styles.applyButton}
                text={loc(applyButtonText)}
                onClick={onApplyClick}
                data-is-focusable={false}
            />
        );
    }
    isFilterOpened();
    return optionToRender;
}

function onKeyDown(ev: React.KeyboardEvent<HTMLDivElement>): void {
    switch (ev.which) {
        case KeyCodes.enter:
            onApplyClick();
            return;
    }
}
